if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initMobileStickyAtc();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.primaryAtcObserver?.disconnect();
        this.primaryAtcStateObserver?.disconnect();
        this.chatStickyResizeObserver?.disconnect();
        if (this.handleMobileStickyVisibility) {
          window.removeEventListener('scroll', this.handleMobileStickyVisibility);
          window.removeEventListener('resize', this.handleMobileStickyVisibility);
        }
        if (this.handleMobileStickyFormSync) {
          this.removeEventListener('change', this.handleMobileStickyFormSync);
          this.removeEventListener('input', this.handleMobileStickyFormSync);
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }
         
          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          this.syncMobileStickyAtc(html);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
          console.log(variant.featured_media);
          
          this.updateMultipleImage(variant);
        };
      }

      updateMultipleImage(variant){
        var variant = variant;
        var proid= "#gall-"+variant.featured_image.product_id;
        if(variant.featured_media != null && variant.featured_media.alt != null){
          $(proid+' [data-thumbnail-color]').hide();
          $('product-modal img.global-media-settings').hide();
          
          $(proid+' [data-thumbnail-color="default"]').show();
          $('product-modal .global-media-settings[alt="default"]').show();
          
          var selected_color = variant.featured_media.alt;
          var selected_colorf = selected_color.toLowerCase();
          
          var thumbnail_selector = proid+' [data-thumbnail-color="'+selected_colorf+'"]';
          $(thumbnail_selector).show();
          
          var globallmodal = 'product-modal .global-media-settings[alt="'+selected_colorf+'"]';
          $(globallmodal).show();
    
          $(".varide").hide();
          $("#vari-"+variant.id).show();
          
        }else{
          $(proid+' [data-thumbnail-color]').show();
          $('product-modal img.global-media-settings').show();
        }
        if($("body").hasClass("template-product")) {
        $("html, body").animate({ scrollTop: 0 }, "fast");
        }
        
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        this.syncMobileStickyAtc();

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      initMobileStickyAtc() {
        this.mobileStickyAtcBar = this.querySelector('[data-mobile-sticky-atc-bar]');
        this.mobileStickyAtcButton = this.querySelector('[data-mobile-sticky-atc-trigger]');
        if (!this.mobileStickyAtcBar || !this.mobileStickyAtcButton || this.mobileStickyAtcBound) return;

        this.handleMobileStickyFormSync = (event) => {
          if (!event.target.closest('variant-selects, .custom-jersey-fields, product-form')) return;
          this.syncMobileStickyAtc();
        };

        this.mobileStickyAtcButton.addEventListener('click', () => {
          const submitButton = this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          const selectedOption = this.getSelectedOptionSummary();
          const personalization = this.getPersonalizationSummary();

          if (!selectedOption?.value && selectedOption?.expectsSelection) {
            this.focusFirstStickyActionTarget();
            return;
          }

          if (personalization?.required && !personalization?.complete) {
            this.focusFirstStickyActionTarget();
            return;
          }

          if (!submitButton) {
            this.querySelector('.product-form-block')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }

          const isDisabled =
            submitButton.hasAttribute('disabled') || submitButton.getAttribute('aria-disabled') === 'true';

          if (isDisabled) {
            this.focusFirstStickyActionTarget();
            return;
          }

          submitButton.click();
        });

        this.handleMobileStickyVisibility = this.updateMobileStickyVisibility.bind(this);
        window.addEventListener('scroll', this.handleMobileStickyVisibility, { passive: true });
        window.addEventListener('resize', this.handleMobileStickyVisibility);
        this.addEventListener('change', this.handleMobileStickyFormSync);
        this.addEventListener('input', this.handleMobileStickyFormSync);

        this.mobileStickyAtcBound = true;
        if ('ResizeObserver' in window) {
          this.chatStickyResizeObserver = new ResizeObserver(() => this.updateChatStickyOffset());
          this.chatStickyResizeObserver.observe(this.mobileStickyAtcBar);
        }
        this.syncMobileStickyAtc();
      }

      syncMobileStickyAtc(sourceDocument = this) {
        if (!this.mobileStickyAtcButton) return;

        const priceSource = this.getNodeById(sourceDocument, `price-${this.sectionId}`);
        const priceDestination = this.querySelector('[data-mobile-sticky-atc-price]');
        if (priceSource && priceDestination) {
          priceDestination.innerHTML = priceSource.innerHTML;
        }

        const sourceButton = this.getNodeById(sourceDocument, `ProductSubmitButton-${this.sectionId}`);
        const liveButton = this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        const buttonForState = sourceButton || liveButton;
        const stickyText = this.querySelector('[data-mobile-sticky-atc-text]');
        const selectedOption = this.getSelectedOptionSummary(sourceDocument);
        const selectionWrap = this.querySelector('[data-mobile-sticky-atc-selection]');
        const selectionLabel = this.querySelector('[data-mobile-sticky-atc-option-label]');
        const selectionValue = this.querySelector('[data-mobile-sticky-atc-option-value]');
        const personalizationNode = this.querySelector('[data-mobile-sticky-atc-personalization]');
        const personalization = this.getPersonalizationSummary(sourceDocument);

        if (selectionWrap && selectionLabel && selectionValue) {
          const hasSelection = !!selectedOption?.value;
          selectionWrap.hidden = !hasSelection;
          selectionLabel.textContent = selectedOption?.label || '';
          selectionValue.textContent = selectedOption?.value || '';
        }

        if (personalizationNode) {
          personalizationNode.hidden = !personalization?.summary;
          personalizationNode.textContent = personalization?.summary || '';
        }

        const sourceButtonText = buttonForState?.querySelector('span')?.textContent?.trim();
        if (stickyText && sourceButtonText) {
          stickyText.textContent = sourceButtonText;
        }

        this.mobileStickyAtcButton.classList.toggle('loading', buttonForState?.classList.contains('loading'));

        const needsSelection = !selectedOption?.value && selectedOption?.expectsSelection;
        const needsPersonalization = personalization?.required && !personalization?.complete;
        let isDisabled =
          !buttonForState ||
          buttonForState.hasAttribute('disabled') ||
          buttonForState.getAttribute('aria-disabled') === 'true';

        if (needsSelection) {
          isDisabled = false;
          if (stickyText) stickyText.textContent = 'Selecciona talla';
        } else if (needsPersonalization) {
          isDisabled = false;
          if (stickyText) stickyText.textContent = 'Completa personalizacion';
        } else if (!buttonForState && stickyText) {
          stickyText.textContent = 'Agregar al carrito';
        }

        this.mobileStickyAtcButton.toggleAttribute('disabled', isDisabled);
        this.mobileStickyAtcButton.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        this.mobileStickyAtcButton.classList.toggle('is-guidance-state', needsSelection || needsPersonalization);
        this.observePrimaryAtc(liveButton);
        this.updateMobileStickyVisibility();
      }

      observePrimaryAtc(sourceButton) {
        if (this.primaryAtcObserver) {
          this.primaryAtcObserver.disconnect();
          this.primaryAtcObserver = null;
        }
        if (this.primaryAtcStateObserver) {
          this.primaryAtcStateObserver.disconnect();
          this.primaryAtcStateObserver = null;
        }

        this.primaryAtcButton = sourceButton || this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (!this.primaryAtcButton || !('IntersectionObserver' in window)) return;

        this.primaryAtcObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.primaryAtcVisible = !!entry?.isIntersecting && entry.intersectionRatio >= 0.35;
            this.updateMobileStickyVisibility();
          },
          {
            threshold: [0, 0.35, 0.75, 1],
          }
        );

        this.primaryAtcObserver.observe(this.primaryAtcButton);

        if (!('MutationObserver' in window)) return;
        this.primaryAtcStateObserver = new MutationObserver(() => {
          this.syncMobileStickyAtc();
        });
        this.primaryAtcStateObserver.observe(this.primaryAtcButton, {
          attributes: true,
          attributeFilter: ['class', 'disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
        });
      }

      updateMobileStickyVisibility() {
        if (!this.mobileStickyAtcBar) return;

        const sourceButton = this.primaryAtcButton || this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        let isVisible = !!this.primaryAtcVisible;

        if (sourceButton) {
          const rect = sourceButton.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
          const ratio = rect.height > 0 ? visibleHeight / rect.height : 0;
          isVisible = isVisible || ratio >= 0.35;
        }

        this.mobileStickyAtcBar.classList.toggle('is-hidden', isVisible);
        this.updateChatStickyOffset();
      }

      updateChatStickyOffset() {
        if (!this.mobileStickyAtcBar || !document.body.classList.contains('template-product')) return;

        const bar = this.mobileStickyAtcBar;
        const style = getComputedStyle(bar);
        const visible = !bar.classList.contains('is-hidden') && style.display !== 'none' && style.visibility !== 'hidden';
        const offset = visible
          ? Math.max(20, Math.ceil(bar.getBoundingClientRect().height + (parseFloat(style.bottom) || 0) + 16))
          : 20;
        const value = `${offset}px`;
        if (document.body.style.getPropertyValue('--mijersey-chat-sticky-offset') !== value) {
          document.body.style.setProperty('--mijersey-chat-sticky-offset', value);
        }
      }

      getSelectedOptionSummary(sourceDocument = this) {
        const variantRoot =
          sourceDocument.querySelector?.(`#variant-selects-${this.sectionId}`) ||
          sourceDocument.querySelector?.('variant-selects');

        if (!variantRoot) return null;

        const options = [];

        variantRoot.querySelectorAll('.product-form__input').forEach((inputGroup) => {
          const rawLabel =
            inputGroup.querySelector('legend')?.childNodes?.[0]?.textContent ||
            inputGroup.querySelector('label.form__label')?.textContent ||
            inputGroup.querySelector('label')?.textContent ||
            '';

          const normalizedLabel = rawLabel.replace(/[:\s]+$/g, '').trim();
          if (!normalizedLabel) return;

          let value = '';
          const checkedRadio = inputGroup.querySelector('input[type="radio"]:checked');
          const select = inputGroup.querySelector('select');

          if (checkedRadio) {
            value = (checkedRadio.dataset.displayValue || checkedRadio.value).trim();
          } else if (select) {
            const selectedOption = select.selectedOptions?.[0];
            value = (selectedOption?.dataset.displayValue || select.value).trim();
          }

          options.push({
            label: normalizedLabel,
            value,
          });
        });

        if (!options.length) return null;

        const preferredOption =
          options.find(({ label }) => /talla|size|tamano|tamaño/i.test(label)) ||
          options.find(({ label }) => !/nombre y numero|nombre y número|personalizar/i.test(label)) ||
          options[0];

        return {
          label: preferredOption.label,
          value: preferredOption.value,
          expectsSelection: /talla|size|tamano|tamaño/i.test(preferredOption.label),
        };
      }

      getPersonalizationSummary(sourceDocument = this) {
        const variantRoot =
          sourceDocument.querySelector?.(`#variant-selects-${this.sectionId}`) ||
          sourceDocument.querySelector?.('variant-selects');
        const nameInput = sourceDocument.querySelector?.(`#custom-name-${this.sectionId}`);
        const numberInput = sourceDocument.querySelector?.(`#custom-number-${this.sectionId}`);

        if (!variantRoot || (!nameInput && !numberInput)) return null;

        const normalizeOptionValue = (value) =>
          String(value || '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

        let personalizationEnabled = false;
        variantRoot.querySelectorAll('.product-form__input').forEach((inputGroup) => {
          const rawLabel =
            inputGroup.querySelector('legend')?.childNodes?.[0]?.textContent ||
            inputGroup.querySelector('label.form__label')?.textContent ||
            '';
          const normalizedLabel = rawLabel.replace(/[:\s]+$/g, '').trim().toLowerCase();
          if (normalizedLabel !== 'nombre y numero' && normalizedLabel !== 'nombre y número' && normalizedLabel !== 'personalizar') return;

          const checkedRadio = inputGroup.querySelector('input[type="radio"]:checked');
          const select = inputGroup.querySelector('select');
          const value = normalizeOptionValue(checkedRadio?.value || select?.value || '');
          personalizationEnabled = value === 'si';
        });

        const name = nameInput?.value?.trim() || '';
        const number = numberInput?.value?.trim() || '';

        if (!personalizationEnabled) return { required: false, complete: true, summary: '' };

        const complete = !!name || !!number;
        const summary = [name ? `Nombre: ${name}` : '', number ? `Número: ${number}` : '']
          .filter(Boolean)
          .join(' · ');

        return {
          required: true,
          complete,
          summary,
        };
      }

      getNodeById(sourceDocument, id) {
        if (!sourceDocument) return null;
        if (typeof sourceDocument.getElementById === 'function') {
          return sourceDocument.getElementById(id);
        }
        return sourceDocument.querySelector?.(`#${id}`) || null;
      }

      focusFirstStickyActionTarget() {
        const selectedOption = this.getSelectedOptionSummary();
        const personalization = this.getPersonalizationSummary();

        if (!selectedOption?.value && selectedOption?.expectsSelection) {
          const sizeGroup = Array.from(this.querySelectorAll('.product-form__input')).find((inputGroup) => {
            const rawLabel =
              inputGroup.querySelector('legend')?.childNodes?.[0]?.textContent ||
              inputGroup.querySelector('label.form__label')?.textContent ||
              '';
            return /talla|size|tamano|tamaño/i.test(rawLabel);
          });

          const firstInteractiveControl = sizeGroup?.querySelector('input, select, button, [tabindex]');
          this.highlightStickyTarget(sizeGroup);
          sizeGroup?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInteractiveControl?.focus({ preventScroll: true });
          return;
        }

        if (personalization?.required && !personalization?.complete) {
          const personalizationBox = this.querySelector(`#custom-jersey-fields-${this.sectionId}`);
          const nameInput = this.querySelector(`#custom-name-${this.sectionId}`);
          const numberInput = this.querySelector(`#custom-number-${this.sectionId}`);
          const targetInput = !nameInput?.value?.trim() ? nameInput : numberInput;

          this.highlightStickyTarget(personalizationBox);
          targetInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetInput?.focus({ preventScroll: true });
          return;
        }

        this.querySelector('.product-form-block')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      highlightStickyTarget(target) {
        if (!target) return;
        target.classList.remove('is-guided-highlight');
        // Force restart so repeated taps still flash the destination.
        void target.offsetWidth;
        target.classList.add('is-guided-highlight');
        clearTimeout(this.highlightStickyTargetTimeout);
        this.highlightStickyTargetTimeout = setTimeout(() => {
          target.classList.remove('is-guided-highlight');
        }, 1800);
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
